/**
 * Let a voicing hold the original for the moment it needs to start.
 *
 * A fragment's audio does not begin the instant play() is called: the element resumes, and the
 * sound arrives a couple of hundred milliseconds later. The first attempt at fixing this seeked
 * the voicing forward to wherever the original had got to, which starts it mid-word — the jumpy
 * playback that was reported. A voicing has to start at its beginning; it is a sentence.
 *
 * So the other side gives: the original waits. It pauses for as long as the voicing needs to
 * make its first sound, then both run together from the same moment. The wait is short and it
 * happens in the gap between two cues, where nothing is being said in either language.
 *
 * A hold is a claim, not a command — several fragments may hold at once during a switch, and the
 * original resumes when the last one lets go. Every claim also expires on its own (MAX_HOLD_MS),
 * so a fragment that never manages to play cannot freeze the lecture.
 *
 * THE GATE IS THE OTHER HALF, and it is deliberately not a claim. A claim answers "this clip is
 * starting, give it a moment"; the gate answers "this clip cannot make sound at all yet, nobody
 * may move". The two need different lifetimes: a claim must expire, because a clip that dies
 * mid-start must not freeze the lecture, whereas the gate must NOT expire, because preparing a
 * clip legitimately takes as long as this machine takes and expiring mid-wait is precisely the
 * "it doesn't wait" that was reported. So the gate is a single level-triggered slot, written by
 * the schedule on every tick and by nobody else.
 */

/**
 * How long a claim outlives the last request for it.
 *
 * A claim is REFRESHED by every holdPlayback call, so this is not a budget for the wait — it
 * is how quickly the original resumes once whoever was waiting stops asking (or disappears).
 */
const CLAIM_TTL_MS = 1500;

/**
 * How long an armed gate survives if no tick ever adopts it.
 *
 * Arming happens in a click handler, before the schedule has looked at anything. Normally the
 * next tick (25ms later) replaces it with a real decision. This TTL only covers the case where
 * that never happens — a click while the schedule is not mounted, say — so that a gesture can
 * never freeze the editor on its own.
 */
const ARM_TTL_MS = 1500;

/** The sentinel the gate holds between a gesture arming it and the first tick deciding. */
const ARMED = "__armed__";

type Listener = (held: boolean) => void;

const holders = new Map<string, ReturnType<typeof setTimeout>>();
const listeners = new Set<Listener>();

/** The cue the world is currently stopped for, or null. Only useVoicingSchedule writes it. */
let gate: string | null = null;
let armTimer: ReturnType<typeof setTimeout> | null = null;

const heldNow = (): boolean => holders.size > 0 || gate !== null;

let lastNotified = false;
/** Depth of the current batch; while it is above zero nothing is announced. */
let batchDepth = 0;

const emit = () => {
    const held = heldNow();
    if (held === lastNotified) return;
    lastNotified = held;
    listeners.forEach((fn) => fn(held));
};

const notify = () => {
    if (batchDepth > 0) return;
    emit();
};

/**
 * ONE ANNOUNCEMENT PER DECISION, NOT ONE PER STEP.
 *
 * The schedule opens the gate and then, further down the same tick, claims the starting clip's
 * hold. Both are correct; the sequence between them is not a state the world should ever see. But
 * React 17 does not batch setState called from a setInterval, so each of those rendered on its own:
 * the original was told to play and, a frame later, to pause. @dschoon/react-waves acts on the
 * `playing` prop from componentWillReceiveProps (dist/index.es.js:9336), so that pair reached the
 * media element as play() immediately followed by pause() — audible as a click or a stutter at
 * every cue boundary, and visible in the console as
 * "AbortError: The play() request was interrupted by a call to pause()".
 *
 * Wrapping a tick in this makes the intermediate states unobservable: subscribers are told only
 * whether the world ended the tick held, and only if that differs from how it started.
 */
export const batchHolds = <T>(fn: () => T): T => {
    batchDepth += 1;
    try {
        return fn();
    } finally {
        batchDepth -= 1;
        if (batchDepth === 0) emit();
    }
};

export const subscribeHold = (fn: Listener): (() => void) => {
    listeners.add(fn);
    fn(heldNow());
    return () => {
        listeners.delete(fn);
    };
};

export const holdPlayback = (id: string, ttlMs: number = CLAIM_TTL_MS): void => {
    const existing = holders.get(id);
    if (existing !== undefined) {
        clearTimeout(existing);
    }
    holders.set(
        id,
        setTimeout(() => {
            holders.delete(id);
            notify();
        }, ttlMs),
    );
    notify();
};

export const releasePlayback = (id: string): void => {
    const timer = holders.get(id);
    if (timer === undefined) return;
    clearTimeout(timer);
    holders.delete(id);
    notify();
};

/**
 * Stop the world for a cue that cannot make sound yet — or let it go.
 *
 * Level-triggered: the schedule calls this on EVERY tick with the cue it is waiting for, or null.
 * There is no expiry, by design (see the note at the top of this file).
 */
export const setVoicingGate = (waitingFor: string | null): void => {
    if (armTimer) {
        clearTimeout(armTimer);
        armTimer = null;
    }
    if (gate === waitingFor) return;
    gate = waitingFor;
    notify();
};

/**
 * Stop the world NOW, from inside a gesture.
 *
 * A click sets the fragment and starts playback synchronously; the schedule only looks 25ms
 * later. Those milliseconds are the lecturer talking over a translation that has not begun —
 * small, but audible, and exactly what the click path was reported for. Arming closes the gate in
 * the same event as the click; the next tick then replaces the sentinel with a real decision.
 */
export const armVoicingGate = (ttlMs: number = ARM_TTL_MS): void => {
    if (gate === null) {
        gate = ARMED;
        notify();
    }
    if (armTimer) clearTimeout(armTimer);
    armTimer = setTimeout(() => {
        armTimer = null;
        if (gate === ARMED) {
            gate = null;
            notify();
        }
    }, ttlMs);
};

/** Drop every claim — used when playback stops, so nothing survives into the next session. */
export const clearHolds = (): void => {
    if (armTimer) {
        clearTimeout(armTimer);
        armTimer = null;
    }
    holders.forEach((timer) => clearTimeout(timer));
    holders.clear();
    gate = null;
    notify();
};

export const isHeld = (): boolean => heldNow();
